import { isDevelopmentEnv } from "./helpers";

// The keycode values for keyboard events
export enum KeyCodes {
  Enter = 13,
  Esc = 27
}

export const CenterPartner = {
  InternalBooking: "internal_booking"
} as const;

// eslint-disable-next-line no-redeclare -- intentional value/type declaration merge
export type CenterPartner = (typeof CenterPartner)[keyof typeof CenterPartner];

// The various routes available
export const RouteNames = {
  SignIn: "SignIn",
  SignInCallback: "SignInCallbackCallback",
  SignInCallbackOBO: "SignInCallbackCallbackOBO",
  SignInCallbackFunction: "SignInCallbackFunction",
  Home: "Home",
  LoginRenewal: "Login/Renewal",
  LoginMedicalQuestionnaire: "Login/MedicalQuestionnaire",
  LoginLmpUpdate: "Login/LmpUpdate",
  LoginOBO: "Login/OBO",
  Sponsored: "Sponsored",
  DashboardScans: "Dashboard/Scans",
  DashboardMessages: "Dashboard/Messages",
  DashboardReports: "Dashboard/Reports",
  DashboardDocuments: "Dashboard/Documents",
  DashboardInvoices: "Dashboard/Invoices",
  DigitalReport: "Dashboard/Reports/DigitalReport",
  DashboardReferrals: "Dashboard/Referrals",
  DashboardAccount: "Dashboard/Account",
  Join: "Join",
  ForgotPassword: "ForgotPassword",
  ForgotPasswordChange: "ForgotPassword/Change",
  ForgotPasswordConfirm: "ForgotPassword/Confirm",
  CATResults: "CAT Results",
  CATResultsSignUp: "CAT Results Sign Up",
  CATQuestionnaire: "CAT Questionnaire",
  Flow: "Flow",
  InitialSetupTOS: "InitialSetup/TOS",
  InitialSetupSetPassword: "InitialSetup/SetPassword",
  BookingSelectPlan: "SelectPlan",
  BookingScheduleScan: "ScheduleScan",
  BookingChangeLocationSchedule: "ChangeLocationSchedule",
  MammoFileUpload: "MammoFileUpload",
  BookingReserveAppointment: "ReserveAppointment",
  BookingError: "BookingError",
  SignUpReferral: "Signup/Referral",
  SignUpClaimOffer: "Signup/ClaimOffer",
  SignUpClaimOfferSponsored: "Signup/ClaimOfferSponsored",
  BookingScanConfirmation: "ScanConfirmation",
  BookingOnboardingConfirmation: "OnboardingConfirmation",
  Viewer: "Viewer",
  MQBase: "MQBase",
  ScheduleAction: "ScheduleAction",
  PaymentAuthorize: "PaymentAuthorize",
  LinkAssociation: "LinkAssociation",
  LmpUpdate: "LmpUpdate"
};

export const BookingRoutePaths = {
  SignUp: "sign-up",
  BookScan: "book-scan",
  Renewal: "renewal",
  Referral: "referral",
  Invite: "invite",
  Sponsored: "sponsored"
};

// Query flag for ScheduleAction's cancel-only entry (a never-booked, WasDeferred package).
export const SCHEDULE_ACTION_CANCEL_ONLY_PARAM = "cancelOnly";

export const INELIGIBLE_US_STATES = ["AR"];

export const States: { [key: string]: string } = {
  AL: "Alabama",
  AK: "Alaska",
  AS: "American Samoa", // Territory
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia", // Federal District
  FM: "Micronesia", // Freely associated state
  FL: "Florida",
  GA: "Georgia",
  GU: "Guam", // Territory
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MH: "Marshall Islands", // Freely associated state
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  MP: "Northern Mariana Islands", // Territory
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PW: "Palau", // Freely associated state
  PA: "Pennsylvania",
  PR: "Puerto Rico", // Territory
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VI: "U.S. Virgin Islands", // Territory
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming"
};

export const timezoneCountryMap = {
  "Europe/London": {
    u: 0,
    d: 60,
    c: ["GB", "GG", "IM", "JE"]
  },
  "Europe/Belfast": {
    a: "Europe/London",
    c: ["GB"],
    r: 1
  },
  "GB": {
    a: "Europe/London",
    c: ["GB"],
    r: 1
  },
  "GB-Eire": {
    a: "Europe/London",
    c: ["GB"],
    r: 1
  },
  "America/Boise": {
    u: -420,
    d: -360,
    c: ["US"]
  },
  "America/Chicago": {
    u: -360,
    d: -300,
    c: ["US"]
  },
  "Pacific/Honolulu": {
    u: -600,
    c: ["US", "UM"]
  },
  "America/Juneau": {
    u: -540,
    d: -480,
    c: ["US"]
  },
  "America/Kentucky/Louisville": {
    u: -300,
    d: -240,
    c: ["US"]
  },
  "America/Kentucky/Monticello": {
    u: -300,
    d: -240,
    c: ["US"]
  },
  "America/Adak": {
    u: -600,
    d: -540,
    c: ["US"]
  },
  "America/Anchorage": {
    u: -540,
    d: -480,
    c: ["US"]
  },
  "America/Denver": {
    u: -420,
    d: -360,
    c: ["US"]
  },
  "America/Detroit": {
    u: -300,
    d: -240,
    c: ["US"]
  },
  "America/Indiana/Indianapolis": {
    u: -300,
    d: -240,
    c: ["US"]
  },
  "America/Indiana/Knox": {
    u: -360,
    d: -300,
    c: ["US"]
  },
  "America/Indiana/Marengo": {
    u: -300,
    d: -240,
    c: ["US"]
  },
  "America/Indiana/Petersburg": {
    u: -300,
    d: -240,
    c: ["US"]
  },
  "America/Indiana/Tell_City": {
    u: -360,
    d: -300,
    c: ["US"]
  },
  "America/Indiana/Vevay": {
    u: -300,
    d: -240,
    c: ["US"]
  },
  "America/Indiana/Vincennes": {
    u: -300,
    d: -240,
    c: ["US"]
  },
  "America/Indiana/Winamac": {
    u: -300,
    d: -240,
    c: ["US"]
  },
  "America/Los_Angeles": {
    u: -480,
    d: -420,
    c: ["US"]
  },
  "America/Menominee": {
    u: -360,
    d: -300,
    c: ["US"]
  },
  "America/Metlakatla": {
    u: -540,
    d: -480,
    c: ["US"]
  },
  "America/New_York": {
    u: -300,
    d: -240,
    c: ["US"]
  },
  "America/Nome": {
    u: -540,
    d: -480,
    c: ["US"]
  },
  "America/North_Dakota/Beulah": {
    u: -360,
    d: -300,
    c: ["US"]
  },
  "America/North_Dakota/Center": {
    u: -360,
    d: -300,
    c: ["US"]
  },
  "America/North_Dakota/New_Salem": {
    u: -360,
    d: -300,
    c: ["US"]
  },
  "America/Phoenix": {
    u: -420,
    c: ["US", "CA"]
  },
  "America/Sitka": {
    u: -540,
    d: -480,
    c: ["US"]
  },
  "America/Yakutat": {
    u: -540,
    d: -480,
    c: ["US"]
  },
  "US/Arizona": {
    a: "America/Phoenix",
    c: ["US"],
    r: 1
  },
  "US/Hawaii": {
    a: "Pacific/Honolulu",
    c: ["US"],
    r: 1
  }
} as {
  [key: string]: {
    a?: string;
    r?: number;
    u?: number;
    c?: string[];
  };
};

export const UKRegions: { [key: string]: string } = {
  LDN: "London",
  NE: "North East",
  NW: "North West",
  YH: "Yorkshire",
  EM: "East Midlands",
  WM: "West Midlands",
  SE: "South East",
  EE: "East of England",
  SW: "South West",
  SCT: "Scotland",
  IE: "Ireland",
  NI: "Northern Ireland",
  WAL: "Wales"
};

// The colors we use.
// If possible use CSS to style instead
// Try to keep in sync with _variables.scss
export const Colors = {
  Yellow: "#ffc000",
  Navy: "#0a0f1e",
  Blue: "#B8DCE0",
  Butter: "#fffef2",
  White: "#ffffff",

  // Secondary
  Pink: "#ff77ab",
  Red: "#B8DCE0",
  DarkBlue: "#4683d0",
  Purple: "#7e59c2",

  // Inferred colours. Not formally listed anywhere but used...
  Yellow2: "#ffc71d",
  Butter2: "#fbf5d8",
  White2: "#B8DCE0",
  Black: "#000000",
  AlertRed: "#f55b4b",
  LightGrey: "#6c6f73",
  Navy5: "#f2f2f2",
  Navy25: "#cecfd2",
  Navy50: "#84878d",
  Navy75: "#474b56",

  // CAT Colors
  CatLow: "#d8d9db",
  CatMedium: "#4683d0",
  CatHigh: "#f55b4b"
};

export const Times = {
  RenewalIntervalMs: 2 * 60 * 1000,
  InactiveLogoutIntervalMs: isDevelopmentEnv()
    ? 120 * 60 * 1000
    : 15 * 60 * 1000,
  DefaultDebounceIntervalMs: 1000,
  InterstitialLeaveDurationMS: 250,
  DefaultShortAnimationLoadingMS: 2000,
  TransitionDurationMS: 250, // Keep this in sync with CSS
  TooltipAppearDurationMS: 250, // Keep this in sync with CSS
  ShortToastDurationMs: 3000,
  DefaultToastDurationMs: 15000,
  LogoutNoticeDurationMs: 5000,
  // Used to indicate the toast message doesn't want a timer queued for it
  ToastNoTimerDuration: -1
};

export enum FieldType {
  Text = "text",
  Email = "email",
  Telephone = "tel",
  Number = "number",
  Password = "password",
  Checkbox = "checkbox",
  CheckboxGroup = "checkboxGroup",
  Radio = "radio",
  DateTime = "dateTime",
  Promo = "promo",
  Label = "label",
  CreditCard = "creditCard",
  Spacer = "spacer",
  LocationSelector = "locationSelector",
  DropDown = "dropDown"
}
